import YAML from 'yaml';

import Daisy from '../assets/images/Daisy.webp';
import Tulip from '../assets/images/Tulip.webp';
import Cat from '../assets/images/cat.webp';
import HelloKittyBride from '../assets/images/hellokitty_bride.webp';
import HelloKittyGroom from '../assets/images/hellokitty_groom.webp';
import Pic1 from '../assets/images/pages/1_DSC00770.webp';
import Pic2 from '../assets/images/pages/2_DSC01398.webp';
import Pic3 from '../assets/images/pages/3_DSC01332.webp';
import Pic4 from '../assets/images/pages/4_DSC00054.webp';
import Pic5 from '../assets/images/pages/5_DSC00622.webp';
// Translation locales
import localeEn from '../assets/locales/locale_en.yaml';
import localeZh from '../assets/locales/locale_zh.yaml';

const imagesToPreload = [
  Pic1,
  Pic2,
  Pic3,
  Pic4,
  Pic5,
  Daisy,
  Tulip,
  Cat,
  HelloKittyBride,
  HelloKittyGroom,
];

const fontsToPreload = [
  { family: 'CormorantGaramond', weight: 'normal' },
  { family: 'CormorantGaramond', weight: 'bold' },
  { family: 'Raleway', weight: 'normal' },
  { family: 'Raleway', weight: 'bold' },
  { family: 'Tangerine', weight: 'normal' },
  { family: 'Tangerine', weight: 'bold' },
  { family: 'CormorantInfant', weight: 'normal' },
  { family: 'CormorantInfant', weight: 'bold' },
];

let parsedTranslations = null;
let translationsPromise = null;
const translationListeners = [];

export const getParsedTranslations = () => parsedTranslations;

export const subscribeTranslations = (callback) => {
  if (parsedTranslations) {
    callback(parsedTranslations);
    return () => {};
  }
  translationListeners.push(callback);

  // Automatically trigger translations preloading if someone subscribes
  preloadTranslations();

  return () => {
    const index = translationListeners.indexOf(callback);
    if (index > -1) {
      translationListeners.splice(index, 1);
    }
  };
};

const preloadTranslations = () => {
  if (parsedTranslations) return Promise.resolve(parsedTranslations);
  if (translationsPromise) return translationsPromise;

  translationsPromise = (async () => {
    try {
      const [en_res, zh_res] = await Promise.all([
        fetch(localeEn),
        fetch(localeZh),
      ]);
      const [en_text, zh_text] = await Promise.all([
        en_res.text(),
        zh_res.text(),
      ]);
      const en_parsed = YAML.parse(en_text);
      const zh_parsed = YAML.parse(zh_text);
      parsedTranslations = { en: en_parsed, zh: zh_parsed };
      translationListeners.forEach((listener) => listener(parsedTranslations));
      return parsedTranslations;
    } catch (err) {
      console.error('Error preloading translations:', err);
      throw err;
    }
  })();

  return translationsPromise;
};

const preloadImage = (src) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = () => resolve(src); // Avoid blocking the application if any image fails to load
    img.src = src;
  });
};

const preloadFont = (font) => {
  if (document.fonts && typeof document.fonts.load === 'function') {
    const weightPrefix = font.weight === 'bold' ? 'bold ' : '';
    // Request loading of the font
    return document.fonts
      .load(`${weightPrefix}1em ${font.family}`)
      .catch((err) => {
        console.warn(
          `Failed to preload font ${font.family} (${font.weight}):`,
          err,
        );
      });
  }
  return Promise.resolve();
};

export const preloadResources = async (onProgress) => {
  const total = imagesToPreload.length + fontsToPreload.length + 1; // 25 + 8 + 1 = 34
  let loaded = 0;

  const tick = () => {
    loaded += 1;
    if (onProgress) {
      onProgress(Math.round((loaded / total) * 100));
    }
  };

  // Trigger initial progress (0%)
  if (onProgress) {
    onProgress(0);
  }

  const preloadImageWithTick = async (src) => {
    await preloadImage(src);
    tick();
  };

  const preloadFontWithTick = async (font) => {
    await preloadFont(font);
    tick();
  };

  const preloadTranslationsWithTick = async () => {
    await preloadTranslations();
    tick();
  };

  try {
    // Start preloading images, fonts, and translations in parallel
    const imagePromises = imagesToPreload.map(preloadImageWithTick);
    const fontPromises = fontsToPreload.map(preloadFontWithTick);
    const translationsPromise = preloadTranslationsWithTick();

    await Promise.all([...imagePromises, ...fontPromises, translationsPromise]);

    // Also wait for document.fonts.ready to be fully safe
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
  } catch (error) {
    console.error('Error preloading resources:', error);
  }
};
